import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import {
  jsonDeepClone,
  SiaSearchBoxComponent,
  SiaSelectBoxDataService,
  SiaTranslatePipe,
} from '@sia/shared';
import { SiaBaseEditorSignalComponent } from '../sia-base-editor/sia-base-editor-signal.component';
import { SiaCheckBoxComponent } from '../sia-check-box/sia-check-box.component';

@Component({
  selector: 'sia-list-selection',
  templateUrl: './sia-list-selection.component.html',
  styleUrls: ['./sia-list-selection.component.scss'],
  standalone: true,
  imports: [SiaCheckBoxComponent, SiaSearchBoxComponent, SiaTranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiaListSelectionComponent extends SiaBaseEditorSignalComponent {
  #siaWidgetService = inject(SiaSelectBoxDataService);
  #startingData: any[] = [];

  dataSource = signal<any[]>([]);
  selectedItems = computed<any[]>(() =>
    this.dataSource().filter((x: any) => x.selected),
  );
  pageMode = input<string>('read');
  foreignKey = input<any>();

  searchExp = signal<string>('');

  onDataSourceLoaded = output<any>();

  #searchEffect = effect(() => {
    const search = this.searchExp().toLowerCase();
    this.dataSource.set(
      this.#startingData.filter(
        (x: any) => !search || x.description.toLowerCase().includes(search),
      ),
    );
  });

  #pageModeEffect = effect(() => {
    const pageMode = this.pageMode();
    this.searchExp.set('');
  });

  selectedItemKeys = [];
  #selectedItems: any[] = [];
  #load = effect(async () => {
    const foreignKey = this.foreignKey();

    const data = await this.#siaWidgetService.loadFullData(
      this.key(),
      foreignKey,
      false,
      true,
      false,
    );
    // Keep shared object references so selected flags survive re-filtering.
    this.dataSource.set(data || []);
    this.#startingData = data ? [...data] : [];
    this.onDataSourceLoaded.emit(jsonDeepClone(data));
  });

  #dataChanged = effect(() => {
    // Track dataSource changes to trigger effect
    this.dataSource();
    // Get all selected items from the complete unfiltered dataset
    this.value.set(this.dataSource().filter((x: any) => x.selected) || []);
    this.valueChanged.emit(this.value());
  });

  onItemSelectionChanged() {
    // Re-apply the filter to trigger the signal and effect chain
    const search = this.searchExp().toLowerCase();
    this.dataSource.set(
      this.#startingData.filter(
        (x: any) => !search || x.description.toLowerCase().includes(search),
      ),
    );
    this.value.set(this.#startingData.filter((x: any) => x.selected) || []);
    this.valueChanged.emit(this.value());
  }

  ngOnDestroy() {
    this.selectedItemKeys = [];
    super.ngOnDestroy();
  }
}
